'use client';

import { incrementTelemetry } from 'core-events';
import { JSX, useContext, useEffect, useRef, useState } from 'react';
import { useErrorBoundary } from 'react-error-boundary';
import { useEvent } from 'react-use';
import { AudioSearchFilterMediaTypes, AudioStockItem } from 'search-gateway-client';
import { twMerge } from 'tailwind-merge';

import { SearchIdentifiers } from '@videoblocks/events-ts/lib/storyblocks/search/records/SearchIdentifiers';

import AudioDuration from '../../elements/AudioDuration';
import AudioMetadata from '../../elements/AudioMetadata';
import { StockItemContext, StockItemDrawerType, StockItemUserData } from '../../elements/StockItemTypes';
import Waveform from '../../elements/Waveform';
import AddToFolderButton from '../../elements/buttons/AddToFolderButton';
import AudioAltVersionsButton from '../../elements/buttons/AudioAltVersionsButton';
import AudioPlayerPlayButton from '../../elements/buttons/AudioPlayerPlayButton';
import DownloadButton from '../../elements/buttons/DownloadButton';
import SearchSimilarButton from '../../elements/buttons/SearchSimilarButton';
import { AUDIO_EVENTS, PlayEvent } from '../../events/AudioEvents';
import { CollapseDrawerEvent, ExpandDrawerEvent } from '../../events/DrawerEvents';
import { AudioAltVersionCollapsible } from './AudioAltVersionCollapsible';
import { ActiveAudioItemContext } from './context/ActiveAudioItemContext';
import StockItemCardContext from './context/StockItemCardContext';

export interface AudioStockItemCardProps {
    hasBeenPlayed?: boolean;
    stockItem: AudioStockItem;
    userData: StockItemUserData;
    context?: StockItemContext;
    drawerContext?: StockItemDrawerType;
    handleAltVersionFetch?: (
        mediaType: AudioSearchFilterMediaTypes,
        collapsedSetId: string,
        numResults?: number,
    ) => Promise<AudioStockItem[]>;
    mediaType?: AudioSearchFilterMediaTypes;
    position?: number; // i.e. index in search results
    searchIdentifiers?: SearchIdentifiers;
}

export default function AudioStockItemCard({
    hasBeenPlayed = false,
    stockItem,
    userData,
    context = StockItemContext.SEARCH,
    drawerContext,
    handleAltVersionFetch,
    position = 1,
    mediaType = 'music',
    searchIdentifiers,
}: AudioStockItemCardProps): JSX.Element {
    const [isPlayed, setIsPlayed] = useState(hasBeenPlayed);
    const [openCollapsible, setOpenCollapsible] = useState(false);
    const { showBoundary } = useErrorBoundary();
    const collapsibleContainerRef = useRef<HTMLDivElement>(null);
    const [altVersionStockItems, setAltVersionStockItems] = useState<AudioStockItem[]>([]);
    const [isHovered, setIsHovered] = useState(false);
    const [isLoggedIn, setIsLoggedIn] = useState(userData.isLoggedIn);

    const { activeAudioItemState, setActiveAudioItemState } = useContext(ActiveAudioItemContext);
    const isPlaying = activeAudioItemState.stockItem?.id === stockItem.id && activeAudioItemState.isPlaying;

    const isInAltVersionsDrawer = () => {
        return context === StockItemContext.DRAWER && drawerContext === StockItemDrawerType.ALT_VERSIONS;
    };

    const isInRecommendedMusicDrawer = () => {
        return context === StockItemContext.DRAWER && drawerContext === StockItemDrawerType.RECOMMENDED_MUSIC;
    };

    const handleAudioItemClick = (isPlaying: boolean) => {
        setActiveAudioItemState({ stockItem, isPlaying });
    };

    const handleOnPlayButtonClick = () => {
        handleAudioItemClick(!isPlaying);
    };

    useEvent(AUDIO_EVENTS.PLAY, (event: PlayEvent) => {
        if (event.src === stockItem.previewUrl && !isPlayed) {
            setIsPlayed(true);
        }
    });

    const getLoggedInState = async () => {
        const isLoggedIn = await userData.getLoggedInState();
        void setIsLoggedIn(isLoggedIn);
    };

    useEffect(() => {
        if (isHovered) {
            void getLoggedInState();
        }
    }, [isHovered]);

    const handleOpenCollapsible = async (open: boolean) => {
        if (!isInAltVersionsDrawer()) {
            setOpenCollapsible(open);
            if (open) {
                // TODO: refactor global state to include alt versions in list of stock items. Forward/Back buttons in GlobalAudioPlayer will skip open items in the drawer rn.
                window.dispatchEvent(new ExpandDrawerEvent(stockItem.id));
                void incrementTelemetry('search.click.altVersions');
                const altVersions = (await handleAltVersionFetch?.(mediaType, stockItem.collapsedSetId)) ?? [];
                setAltVersionStockItems(altVersions?.filter((item) => item.id !== stockItem.id));
            } else {
                window.dispatchEvent(new CollapseDrawerEvent(stockItem.id));
            }
        }
    };

    const sectionRef = useRef<HTMLDivElement>(null);

    return (
        <StockItemCardContext.Provider value={{ stockItemContext: context, drawerContext }}>
            <section
                ref={sectionRef}
                data-testid="audio-card"
                aria-label="Audio Stock Item Card"
                className={twMerge(
                    'group flex w-full m-0 py-3 px-4 z-auto flex-wrap text-black relative lg:h-25 gap-x-3 lg:gap-x-8 lg:flex-nowrap lg:py-4 lg:px-8 hover:bg-blue-100 border-x-4 border-x-transparent',
                    !isPlayed || context === StockItemContext.DETAILS_PLAYER
                        ? 'bg-white'
                        : isPlaying
                          ? 'bg-blue-100'
                          : 'border-x-gray-600',
                    'border-b first:border-t border-y-gray-300',
                )}
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={(e) => {
                    const related = e.relatedTarget as HTMLElement | null;
                    if (related && related instanceof HTMLElement && sectionRef.current?.contains(related)) {
                        return;
                    }
                    // i.e. refresh folders
                    setIsHovered(false);
                }}
                onFocus={() => setIsHovered(true)}
                onBlur={() => setIsHovered(false)}
            >
                <div className={'flex w-full lg:w-1/3 gap-x-3 lg:gap-x-8 justify-between items-center'}>
                    <AudioPlayerPlayButton isPlaying={isPlaying} onClick={handleOnPlayButtonClick} />
                    <AudioMetadata stockItem={stockItem} position={position} />
                    {!isInAltVersionsDrawer() && context !== StockItemContext.BUILDER && (
                        <AudioAltVersionCollapsible
                            currStockItem={stockItem}
                            trigger={<AudioAltVersionsButton stockItem={stockItem} />}
                            stockItems={altVersionStockItems ?? []}
                            // eslint-disable-next-line @typescript-eslint/no-misused-promises
                            onOpenChange={handleOpenCollapsible}
                            open={openCollapsible}
                            // eslint-disable-next-line react-hooks/refs
                            portalContainer={collapsibleContainerRef.current}
                            userData={userData}
                        />
                    )}
                </div>
                <div className="flex w-full h-16 gap-x-4 lg:h-auto lg:w-2/3 lg:gap-x-6 items-center">
                    <Waveform
                        stockItem={stockItem}
                        isPlaying={!!isPlaying}
                        handleAudioItemClick={handleAudioItemClick}
                        className="order-2 lg:order-1"
                    />
                    <AudioDuration duration={stockItem.duration} />
                    <div className="flex items-center gap-x-4 lg:gap-x-6 order-last">
                        <SearchSimilarButton
                            isDisabled={!stockItem.hasVector || context !== StockItemContext.SEARCH}
                            selectedContentType={stockItem.contentType}
                            contentId={stockItem.id}
                        />
                        <AddToFolderButton
                            folderGroup={userData.folderGroup}
                            isLoggedIn={isLoggedIn}
                            rootHovered={isHovered}
                            className="text-black"
                            stockItem={stockItem}
                            searchIdentifiers={searchIdentifiers}
                        />
                        <DownloadButton
                            contentID={stockItem.id}
                            isLoggedIn={isLoggedIn}
                            detailsUrl={stockItem.detailsUrl}
                            previewUrl={stockItem.previewUrl}
                            downloadGroup={userData.downloadGroup}
                            rootHovered={isHovered}
                            iconClassName="w-6 h-6"
                            stockItem={stockItem}
                            position={position}
                            stockblocksId={stockItem.stockblocksId}
                            onError={(message) => showBoundary(new Error(message))}
                        />
                    </div>
                </div>
            </section>
            {!isInAltVersionsDrawer() && <div ref={collapsibleContainerRef} />}
        </StockItemCardContext.Provider>
    );
}
